import { useState, useEffect, useMemo, useCallback } from "react";
import DashboardService from "../../../services/pbumku/DashboardService";

// ─── Helpers ────────────────────────────────────────────────────────────────

const pad = (n) => String(n).padStart(2, "0");

export const fmtDatetime = (date, isEnd = false) => {
  const y = date.getFullYear();
  const m = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  return `${y}-${m}-${d} ${isEnd ? "23:59:59" : "00:00:00"}`;
};

export const fmtDateInput = (date) => {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 10);
};

export const fmtDateShort = (date) => {
  const y = date.getFullYear();
  const m = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  return `${y}-${m}-${d}`;
};

export const fmtTanggalIndo = (str) => {
  const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
  const d = new Date(str);
  return `${pad(d.getDate())} ${BULAN[d.getMonth()]} ${d.getFullYear()}`;
};

// ─── Default period: current month ──────────────────────────────────────────

const buildDefaultPeriod = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  return {
    start: new Date(y, m, 1),
    end: new Date(y, m + 1, 0),
  };
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export const useDashEkspor = () => {
  const service = useMemo(() => new DashboardService(), []);

  // ── Period state ──────────────────────────────────────────────────────────
  const defaultPeriod = buildDefaultPeriod();
  const [start, setStart] = useState(defaultPeriod.start);
  const [end, setEnd] = useState(defaultPeriod.end);
  const [xAxis, setXAxis] = useState(1); // 1 = harian, 2 = bulanan
  const [label, setLabel] = useState(() => {
    const { start: s, end: e } = buildDefaultPeriod();
    const NAMA_BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
    return `${NAMA_BULAN[s.getMonth()]} ${s.getFullYear()}`;
  });

  // ── Summary stats ─────────────────────────────────────────────────────────
  const [jumFreq, setJumFreq] = useState(0);
  const [jumVol, setJumVol] = useState(0);
  const [nilaiIDR, setNilaiIDR] = useState(0);
  const [nilaiUSD, setNilaiUSD] = useState(0);

  // ── Chart data ────────────────────────────────────────────────────────────
  const [frequensi, setFrequensi] = useState([]);
  const [frequensiNegara, setFrequensiNegara] = useState([]);
  const [frequensiUpt, setFrequensiUpt] = useState([]);
  const [frequensiTrader, setFrequensiTrader] = useState([]);
  const [bentuk, setBentuk] = useState([]);
  const [komoditasUnggulan, setKomoditasUnggulan] = useState([]);

  // ── Meta ──────────────────────────────────────────────────────────────────
  const [bulanTahun, setBulanTahun] = useState([]);
  const [tahuns, setTahuns] = useState([]);
  const [namaUPT, setNamaUPT] = useState(null);
  const [isVisibleUpt, setIsVisibleUpt] = useState(false);
  const [isTrader, setIsTrader] = useState(false);

  // ── Common grouping ───────────────────────────────────────────────────────
  const [commonExport, setCommonExport] = useState([]);
  const [commonKategori, setCommonKategori] = useState("kel_ikan");
  const [commonOrderBy, setCommonOrderBy] = useState("jumlah");
  const [commonLimit, setCommonLimit] = useState(5);

  // ── Derived values ────────────────────────────────────────────────────────
  const kdUPT = localStorage.getItem("kdUnit") ?? "";

  // ── Fetch helpers ─────────────────────────────────────────────────────────

  const fetchPeriodData = useCallback(async (s, e, axis) => {
    const [a, b] = [fmtDatetime(s), fmtDatetime(e, true)];

    const [freq, negara, upt, trader, freq2, vol, nilai, bentukRes, komoditas] =
      await Promise.allSettled([
        axis === 1
          ? service.getFrequensi(kdUPT, a, b)
          : service.getFrequensiBulan(kdUPT, a, b),
        service.getFrequensiNegara(kdUPT, a, b),
        service.getFrequensiUpt(kdUPT, a, b),
        service.getFrequensiTrader(kdUPT, a, b),
        service.getJumlahFrequensi(kdUPT, a, b),
        service.getJumlahVolume(kdUPT, a, b),
        service.getJumlahNilai(kdUPT, a, b),
        service.getKetBentuk(kdUPT, a, b),
        service.getKomoditasUnggulan(kdUPT, a, b, 10),
      ]);

    if (freq.status === "fulfilled") setFrequensi(freq.value.data);
    if (negara.status === "fulfilled") setFrequensiNegara(negara.value.data);
    if (upt.status === "fulfilled") setFrequensiUpt(upt.value.data);
    if (trader.status === "fulfilled") setFrequensiTrader(trader.value.data);
    if (freq2.status === "fulfilled") setJumFreq(freq2.value.data);
    if (vol.status === "fulfilled") setJumVol(vol.value.data.totalVolume);
    if (nilai.status === "fulfilled") {
      setNilaiIDR(nilai.value.data.totalNilaiIDR);
      setNilaiUSD(nilai.value.data.totalNilaiUSD);
    }
    if (bentukRes.status === "fulfilled") setBentuk(bentukRes.value.data);
    if (komoditas.status === "fulfilled") setKomoditasUnggulan(komoditas.value.data);
  }, [service, kdUPT]);

  const fetchCommonGrouping = useCallback(async (s, e, kategori, orderBy, limit) => {
    try {
      const res = await service.getCommonGrouping({
        kategori,
        tglAwal: fmtDateShort(s),
        tglAkhir: fmtDateShort(e),
        kdUpt: kdUPT || null,
        limit,
        orderBy,
      });
      setCommonExport(res.data);
    } catch (err) {
      console.error("Gagal fetch grouping:", err);
    }
  }, [service, kdUPT]);

  const fetchMeta = useCallback(async () => {
    const [bulan, tahun, upt] = await Promise.allSettled([
      service.getBulanTahun(),
      service.getTahun(),
      (async () => {
        const excluded = new Set(["00.1", "00.2", "00.3"]);
        if (!excluded.has(kdUPT.trim())) {
          return service.getNamaUPT(kdUPT.trim());
        }
        return null;
      })(),
    ]);

    if (bulan.status === "fulfilled") setBulanTahun(bulan.value.data);
    if (tahun.status === "fulfilled") setTahuns(tahun.value.data);
    if (upt.status === "fulfilled" && upt.value?.data) {
      setNamaUPT(upt.value.data.NM_PENDEK_BARU);
      setIsVisibleUpt(true);
    }

    const role = localStorage.getItem("role");
    setIsTrader(["4", "5"].includes(role));
  }, [service, kdUPT]);

  // ── Effects ───────────────────────────────────────────────────────────────

  // Inisialisasi meta (sekali)
  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  // Re-fetch saat periode berubah
  useEffect(() => {
    fetchPeriodData(start, end, xAxis);
    fetchCommonGrouping(start, end, commonKategori, commonOrderBy, commonLimit);
  }, [start, end, xAxis]);

  // Re-fetch grouping saat filter common berubah
  useEffect(() => {
    fetchCommonGrouping(start, end, commonKategori, commonOrderBy, commonLimit);
  }, [commonKategori, commonOrderBy, commonLimit]);

  // ── Label builder ─────────────────────────────────────────────────────────
  // Aturan:
  //   - awal bulan s/d akhir bulan  → "Februari 2026"
  //   - 1 Jan s/d 31 Des            → "Tahun 2026"
  //   - selain itu                  → "Periode 01 Februari 2026 s/d 28 Februari 2026"

  const NAMA_BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

  const buildLabel = (s, e) => {
    const sy = s.getFullYear(), ey = e.getFullYear();
    const sm = s.getMonth(), em = e.getMonth();
    const sd = s.getDate(), ed = e.getDate();

    const isAwalBulan = sd === 1;
    const isAkhirBulan = ed === new Date(ey, em + 1, 0).getDate();
    const isSameBulan = sy === ey && sm === em;

    // Seluruh bulan
    if (isSameBulan && isAwalBulan && isAkhirBulan) {
      return `${NAMA_BULAN[sm]} ${sy}`;
    }

    // Seluruh tahun (1 Jan - 31 Des)
    const isFullYear = sy === ey && sm === 0 && sd === 1 && em === 11 && ed === 31;
    if (isFullYear) {
      return `Tahun ${sy}`;
    }

    // Periode bebas
    return `Periode ${fmtTanggalIndo(s.toISOString())} s/d ${fmtTanggalIndo(e.toISOString())}`;
  };

  // ── Period change handlers ────────────────────────────────────────────────

  const setPeriodBulan = (value) => {
    const BULAN_MAP = { Januari: 0, Pebruari: 1, Maret: 2, April: 3, Mei: 4, Juni: 5, Juli: 6, Agustus: 7, September: 8, Oktober: 9, November: 10, Desember: 11 };
    const [namaBulan, tahunStr] = value.split(" ");
    const tahun = parseInt(tahunStr);
    const bulan = BULAN_MAP[namaBulan];
    if (bulan === undefined || isNaN(tahun)) return;

    const s = new Date(tahun, bulan, 1);
    const e = new Date(tahun, bulan + 1, 0);
    setStart(s);
    setEnd(e);
    setXAxis(1);
    setLabel(buildLabel(s, e));
  };

  const setPeriodTahun = (value) => {
    const tahun = parseInt(String(value));
    const s = new Date(tahun, 0, 1);
    const e = new Date(tahun, 11, 31);
    setStart(s);
    setEnd(e);
    setXAxis(2);
    setLabel(buildLabel(s, e));
  };

  const setPeriodCustom = (startStr, endStr) => {
    const s = new Date(startStr);
    const e = new Date(endStr);

    if (e < s) { alert("Tanggal akhir harus ≥ tanggal awal."); return; }
    if (s.getFullYear() !== e.getFullYear()) { alert("Periode harus dalam tahun yang sama."); return; }

    const isSameMonth = s.getMonth() === e.getMonth();
    setStart(s);
    setEnd(e);
    setXAxis(isSameMonth ? 1 : 2);
    setLabel(buildLabel(s, e));
  };

  return {
    // period
    start, end, xAxis, label,
    setPeriodBulan, setPeriodTahun, setPeriodCustom,
    // summary
    jumFreq, jumVol, nilaiIDR, nilaiUSD,
    // chart data
    frequensi, frequensiNegara, frequensiUpt, frequensiTrader,
    bentuk, komoditasUnggulan,
    // common grouping
    commonExport, commonKategori, commonOrderBy, commonLimit,
    setCommonKategori, setCommonOrderBy, setCommonLimit,
    // meta
    bulanTahun, tahuns, namaUPT, isVisibleUpt, isTrader,
  };
};